import { computed, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import useUtil from "@/composables/useUtil";
import { exceptionRequestMsg, showToast } from "@/assets/js/util";
import { cotizacionFactorajeService } from "@/services/cotizacion-factoraje.service";
import { useCotizacionFactorajeStore } from "@/store/cotizaciones/factoraje.store";
import { storeToRefs } from "pinia";

export const useCotizacionFactoraje = () => {
  const router = useRouter();
  const { formatoMoneda } = useUtil();
  const {
    state,
    aforo_cantidad,
    tasaDiaria,
    tasaPorPlazo,
    montoARecibir,
    comisionAperturaCantidad,
    comisionDisposicionCantidad,
    tasaCantidad,
  } = storeToRefs(useCotizacionFactorajeStore());

  const { initialState } = useCotizacionFactorajeStore();

  onUnmounted(() => {
    // Reiniciar el estado al salir de la página
    state.value = { ...initialState };
  });

  const fechaFormateada = computed(() => {
    if (!state.value.fecha_pago) return "";
    const opciones = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "2-digit",
    };
    // Cambiamos el año a 2026 como ejemplo
    const fechaAjustada = new Date(state.value.fecha_pago + "T00:00:00");
    return fechaAjustada.toLocaleDateString("es-MX", opciones);
  });

  const regresar = () => {
    router.push({ name: "factoraje" });
  };

  const getCotizacionById = async (cotizacion_id) => {
    try {
      const response = await cotizacionFactorajeService.getById(cotizacion_id);
      return response.data;
    } catch (error) {
      console.error(error);
      exceptionRequestMsg(error);
    }
  };

  return {
    state,
    aforo_cantidad,
    tasaDiaria,
    tasaPorPlazo,
    montoARecibir,
    formatoMoneda,
    //obtenerFechaActual,
    fechaFormateada,
    regresar,
    getCotizacionById,
    comisionAperturaCantidad,
    comisionDisposicionCantidad,
    tasaCantidad,
  };
};

export const useCotizacionFactorajeSave = () => {
  const loading = ref(false);
  const { confirmAction } = useUtil();
  const {
    state,
    aforo_cantidad,
    montoARecibir,
    comisionAperturaCantidad,
    comisionDisposicionCantidad,
    tasaCantidad,
  } = storeToRefs(useCotizacionFactorajeStore());
  const router = useRouter();

  const aforoFueraRango = computed(() => {
    const aforo = Number(state.value.aforo_porcentaje) || 0;
    return aforo > 80;
  });

  const comisionAperturaFueraRango = computed(() => {
    const comisionApertura = Number(state.value.comision_apertura) || 0;
    return comisionApertura < 2;
  });

  const comisionDisposicionFueraRango = computed(() => {
    const comisionDisposicion = Number(state.value.comision_disposicion) || 0;
    return comisionDisposicion < 1;
  });

  const tasaFueraRango = computed(() => {
    const tasa = Number(state.value.tasa) || 0;
    return tasa < 3;
  });

  const conceptosFueraDeRango = computed(() => {
    return (
      aforoFueraRango.value ||
      comisionAperturaFueraRango.value ||
      comisionDisposicionFueraRango.value ||
      tasaFueraRango.value
    );
  });

  const imprimerErrorRango = () => {
    if (aforoFueraRango.value) {
      return "El aforo no debe ser mayor al 80%";
    }
    if (comisionAperturaFueraRango.value) {
      return "La comisión de apertura no debe ser menor al 2%";
    }
    if (comisionDisposicionFueraRango.value) {
      return "La comisión de disposición no debe ser menor al 1%";
    }
    if (tasaFueraRango.value) {
      return "La tasa no debe ser menor al 3%";
    }
  };

  const guardar = async () => {
    loading.value = true;
    try {
      return await cotizacionFactorajeService.store({
        ...state.value,
        aforo_cantidad: aforo_cantidad.value,
        monto_a_recibir: montoARecibir.value,
        comision_apertura_cantidad: comisionAperturaCantidad.value,
        comision_disposicion_cantidad: comisionDisposicionCantidad.value,
        tasa_cantidad: tasaCantidad.value,
      });
    } catch (error) {
      console.error(error);
      exceptionRequestMsg(error);
      throw error; // muy importante
    } finally {
      loading.value = false;
    }
  };

  const confirmaGuardado = async () => {
    const confirmado = await confirmAction({ title: "¿Guardar cotización?" });

    if (!confirmado) return;

    try {
      const response = await guardar();
      showToast("Cotización guardada correctamente", "success");
      console.log(response);
      router.push({
        name: "factoraje-cotizacion-detalle",
        params: { cotizacion_id: response.data.data.token }, // TODO: reemplazar con el ID real
      });
    } catch {
      // ya mostraste el error en guardar(); aquí NO muestras el toast de éxito
    }
  };

  return {
    confirmaGuardado,
    loading,
    imprimerErrorRango,
    conceptosFueraDeRango,
  };
};

export const useCotizacionFactorajeHistorial = () => {
  const cotizaciones = ref([]);
  const loading = ref(false);
  const usuario = ref(undefined);

  const columns = [
    { label: "#", field: "id", sortable: true, tdClass: "text-center" },
    { label: "Cliente", field: "cliente" },
    { label: "Línea", field: "linea", sortable: true, tdClass: "text-center" },
    {
      label: "Monto factura",
      field: "monto_factura",
      sortable: true,
      tdClass: "text-center",
    },
    {
      label: "Aforo",
      field: "aforo",
      sortable: true,
      tdClass: "text-center",
    },
    {
      label: "CxA",
      field: "comision_apertura",
      sortable: true,
      tdClass: "text-center",
    },
    {
      label: "CxD",
      field: "comision_disposicion",
      sortable: true,
      tdClass: "text-center",
    },
    {
      label: "Tasa",
      field: "tasa",
      sortable: true,
      tdClass: "text-center",
    },
    {
      label: "Plazo",
      field: "plazo",
      sortable: true,
      tdClass: "text-center",
    },
    { label: "Fecha curse", field: "fecha_curse", sortable: true },
    { label: "Fecha pago", field: "fecha_pago", sortable: true },
    {
      label: "Monto a recibir",
      field: "monto_a_recibir",
      sortable: true,
      tdClass: "text-center",
    },
    {
      label: "",
      field: "acciones",
      sortable: false,
      tdClass: "d-flex justify-content-center",
    },
  ];

  const getCotizaciones = async () => {
    try {
      const response = await cotizacionFactorajeService.get();
      cotizaciones.value = response.data.cotizacion;
      usuario.value = response.data.usuario;
      console.log(cotizaciones.value);
    } catch (error) {
      console.error(error);
      exceptionRequestMsg(error);
    }
  };

  return {
    cotizaciones,
    loading,
    columns,
    getCotizaciones,
    usuario,
  };
};
